import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import Scrollable from "@/common/components/scrollable"
import Icons from "@/images/signomatic/signtypes"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

interface TypeProps {
  name: string
  imageName: string
  active: boolean
  update: () => void
  onReadMore: (e: React.MouseEvent) => void
  showReadMore: boolean
}

const Type = ({ name, imageName, active, update, onReadMore, showReadMore }: TypeProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("described", { active }, `type-${name}`)} onClick={update}>
      <div className="contents">
        <div className="image">{Icons[imageName] ? React.createElement(Icons[imageName]) : ""}</div>
        <div className="details">
          <h5 className="mb-0">{t(`material_type.${name}`)}</h5>
          {showReadMore && (
            <p className="paragraph-xs mb-0">
              <a onClick={onReadMore}>
                {t("misc.readmore")}
                ...
              </a>
            </p>
          )}
        </div>
        <div className="info-row bottom">
          <SelectedIndicator active={active} />
        </div>
      </div>
    </div>
  )
}

interface TypeDetailsProps {
  name: string
  text: string
  image: string
}

const TypeDetails = ({ name, text, image }: TypeDetailsProps) => {
  const { t } = useTranslation()

  return (
    <div className="described-details type">
      <img src={image} alt="" />
      <h5>{t(`material_type.${name}`)}</h5>
      <div className="description" dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  )
}

interface TypeToolboxStateProps {
  signType: string
  data: SignData
  signState: SignState
  metaProduct: string
  signs: SignType[]
  subToolbox: string
}

interface TypeToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
}

type TypeToolboxProps = TypeToolboxStateProps & ToolboxStripItemProps & TypeToolboxDispatchProps

const TypeToolbox: React.FC<TypeToolboxProps> = ({ sign, data, metaProduct, signs, subToolbox, setSubToolbox }) => {
  const currentSign = subToolbox ? signs.find(sign => `${sign.key}_material` === subToolbox) : null
  const signTypes = data.signtypes

  return (
    <Toolbox tKey="type">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {signTypes?.map(signType => {
          const active = sign.signType === signType
          const showReadMore = signType == "acryl_alu_sign"

          return (
            <Type
              key={signType}
              name={signType}
              imageName={signType}
              active={active}
              update={() => {
                sign.setMetaSignType(metaProduct, signType)

                if (!showReadMore) {
                  setSubToolbox(null)
                } else if (subToolbox && subToolbox !== `${signType}_material`) {
                  setSubToolbox(`${signType}_material`)
                }
              }}
              onReadMore={e => {
                e.stopPropagation()
                e.preventDefault()
                setSubToolbox(`${signType}_material`)
              }}
              showReadMore={showReadMore}
            />
          )
        })}
      </Scrollable>
      {subToolbox && currentSign && (
        <Scrollable>
          <TypeDetails name={currentSign.key} text={currentSign.text} image={currentSign.image} />
        </Scrollable>
      )}
    </Toolbox>
  )
}

export default connect(
  ({ signs, signType, signState, subToolbox, currentSignData, metaProduct }: State) => ({
    signs,
    signType,
    signState,
    subToolbox,
    data: currentSignData,
    metaProduct,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
  }
)(TypeToolbox)
